// monitors.js
import {
  DETAIL_DRAWER_CREATIVE_LIST_PERF,
  PROMOTION_TTI_PERF,
  PROMOTION_TABLE_PERF,
  PROMOTION_DRAWER_PERF,
  KEYWORD_TTI_PERF,
  KEYWORD_LIST_PERF,
  AD_TTI_PERF,
  CREATIVE_TTI_PERF,
  GENERAL_TTI_METRIC_SETTING,
  COMMON_STATIC_TAGS
} from './const'
import PerfMonitor from '@ad/perf-monitor/lib/index.umd'
import { setTTIStage } from 'Src/basic/boot/tti-state'

// common mark names
export const REQUEST_START = 'requestStart'
export const REQUEST_END = 'requestEnd'
export const RENDER_START = 'renderStart'
export const RENDER_END = 'renderEnd'

// promotion mark names
export const BEFORE_ENTRY_RUN = 'beforeEntryRun'
export const APP_MOUNTED = 'appMounted'
export const PROMOTION_MOUNTED = 'promotionMounted'
export const TABLE_MOUNTED = 'tableMounted'

/*
simple request-render metric setting:
------> ... ------> requestStart ------> requestEnd ------> renderEnd ------> ... ------>
                         | ---- request ---- | ---- render  ---- |
                         | --------------- total  -------------- |
 */
const REQUEST_RENDER_METRIC_SETTING = {
  request: [REQUEST_START, REQUEST_END],
  render: [REQUEST_END, RENDER_END],
  total: [REQUEST_START, RENDER_END]
}

/*
* promotion TTI metric setting:
nav.fetchStart ------> nav.requestStart ------> nav.responseStart ------> nav.responseEnd ------> beforeEntryRun ------> promotionMounted ------> tableMounted ------> requestStart ------> requestEnd ------> renderEnd ------>
      | ---- html_connect ---- | ---- html_ttfb ---- | ---- html_download ---- | -- init_script_eval -- | - init_promotion_mount - | - init_table_mount - | -- init_other -- | --- request --- | ---- render  ---- |
      | --------------------------------- html ------------------------------- | ------------------------------------------ init ------------------------------------------- |
      | ----------------------------------------------------------- fmp ---------------------------------------------------------- |
      | ---------------------------------------------------------------------------------------------- total(TTI) ------------------------------------------------------------------------------------------------ |
* */
const PROMOTION_TTI_METRIC_SETTING = {
  // html: html_connect -> html_ttfb -> html_download
  html: ['nav.fetchStart', 'nav.responseEnd'],
  html_connect: ['nav.fetchStart', 'nav.requestStart'],
  html_ttfb: ['nav.requestStart', 'nav.responseStart'],
  html_download: ['nav.responseStart', 'nav.responseEnd'],

  // init: init_script_eval -> init_promotion_mount -> init_table_mount -> init_other
  init: ['nav.responseEnd', REQUEST_START],
  init_script_eval: ['nav.responseEnd', BEFORE_ENTRY_RUN],
  init_promotion_mount: [BEFORE_ENTRY_RUN, PROMOTION_MOUNTED],
  init_table_mount: [PROMOTION_MOUNTED, TABLE_MOUNTED],
  init_other: [TABLE_MOUNTED, REQUEST_START],

  // list request
  request: [REQUEST_START, REQUEST_END],

  // table render
  render: [REQUEST_END, RENDER_END],

  // FMP: html -> init(promotionMounted)
  fmp: ['nav.fetchStart', PROMOTION_MOUNTED],
  // TTI: html -> init -> request -> render
  total: ['nav.fetchStart', RENDER_END]
}

// promotion table perf
export const promotionTableMonitor = new PerfMonitor(
  PROMOTION_TABLE_PERF,
  REQUEST_RENDER_METRIC_SETTING,
  {
    // promotion tti(promotionTTIMonitor) contain this table request and render time, so disable this collection before promotion tti reported
    enable: false
  }
)

// promotion tti perf
export const promotionTTIMonitor = new PerfMonitor(
  PROMOTION_TTI_PERF,
  PROMOTION_TTI_METRIC_SETTING,
  {
    once: true,
    onReported: () => {
      setTimeout(() => { promotionTableMonitor.config({ enable: true }) })
    },
    onMarked: (markName) => {
      setTTIStage(markName, markName === RENDER_END)
    }
  }
)

// promotion detail drawer creative-list perf
export const detailDrawerCreativePerfMonitor = new PerfMonitor(
  DETAIL_DRAWER_CREATIVE_LIST_PERF,
  REQUEST_RENDER_METRIC_SETTING
)

export const QUICK_CHART_REQUEST_START = 'quickChartRequestStart'
export const QUICK_CHART_REQUEST_END = 'quickChartRequestEnd'
export const CREATIVE_LIST_REQUEST_START = 'creativeListRequestStart'
export const CREATIVE_LIST_REQUEST_END = 'creativeListRequestEnd'
export const AD_INFO_REQUEST_START = 'adInfoRequestStart'
export const AD_INFO_REQUEST_END = 'adInfoRequestEnd'
export const REPORTER_CHART_REQUEST_START = 'reporterChartRequestStart'
export const REPORTER_CHART_REQUEST_END = 'reporterChartRequestEnd'
export const REPORTER_TABLE_REQUEST_START = 'reporterTableRequestStart'
export const REPORTER_TABLE_REQUEST_END = 'reporterTableRequestEnd'

export const promotionDrawerDetailMonitor = new PerfMonitor(
  PROMOTION_DRAWER_PERF,
  {
    chart: [QUICK_CHART_REQUEST_START, QUICK_CHART_REQUEST_END],
    creative_list: [CREATIVE_LIST_REQUEST_START, CREATIVE_LIST_REQUEST_END],
    ad_info: [AD_INFO_REQUEST_START, AD_INFO_REQUEST_END],
    total: [
      PerfMonitor.Race([QUICK_CHART_REQUEST_START, CREATIVE_LIST_REQUEST_START, AD_INFO_REQUEST_START]),
      PerfMonitor.All([QUICK_CHART_REQUEST_END, CREATIVE_LIST_REQUEST_END, AD_INFO_REQUEST_END])
    ]
  },
  {
    tags: { tab_id: 'detail' },
    onBeforeReport: (metrics) => ({ metrics: { duration: metrics.total } })
  }
)

export const promotionDrawerLogMonitor = new PerfMonitor(
  PROMOTION_DRAWER_PERF,
  {
    total: [REQUEST_START, REQUEST_END]
  },
  {
    tags: { tab_id: 'optLog' },
    onBeforeReport: (metrics) => ({ metrics: { duration: metrics.total } })
  }
)

export const promotionDrawerReporterMonitor = new PerfMonitor(
  PROMOTION_DRAWER_PERF,
  {
    chart: [REPORTER_CHART_REQUEST_START, REPORTER_CHART_REQUEST_END],
    table: [REPORTER_TABLE_REQUEST_START, REPORTER_TABLE_REQUEST_END],
    total: [
      PerfMonitor.Race([REPORTER_CHART_REQUEST_START, REPORTER_TABLE_REQUEST_START]),
      PerfMonitor.All([REPORTER_CHART_REQUEST_END, REPORTER_TABLE_REQUEST_END])
    ]
  },
  {
    tags: { tab_id: 'reporter' },
    onBeforeReport: (metrics) => ({ metrics: { duration: metrics.total } })
  }
)

export const promotionDrawerDiagnosisMonitor = new PerfMonitor(
  PROMOTION_DRAWER_PERF,
  {
    total: [RENDER_START, RENDER_END]
  },
  {
    tags: { tab_id: 'diagnosisIframe' },
    onBeforeReport: (metrics) => ({ metrics: { duration: metrics.total } })
  }
)

export const PROMOTION_DRAWER_PLAN_REQUEST_START = 'promotionDrawerPlanRequestStart'
export const PROMOTION_DRAWER_PLAN_REQUEST_END = 'promotionDrawerPlanRequestEnd'

export const promotionDrawerPlanMonitor = new PerfMonitor(
  PROMOTION_DRAWER_PERF,
  {
    total: [PROMOTION_DRAWER_PLAN_REQUEST_START, PROMOTION_DRAWER_PLAN_REQUEST_END]
  },
  {
    tags: { tab_id: 'plan' },
    onBeforeReport: (metrics) => ({ metrics: { duration: metrics.total } })
  }
)

export const setPromotionDrawerMonitors = (currNav, isChange = false) => {
  // PerfMonitor 暂不支持动态 MetricSetting，先用这种方式
  if (currNav === 'detail') {
    promotionDrawerDetailMonitor.metricSetting.total = isChange ? [
      PerfMonitor.Race([QUICK_CHART_REQUEST_START, CREATIVE_LIST_REQUEST_START]),
      PerfMonitor.All([QUICK_CHART_REQUEST_END, CREATIVE_LIST_REQUEST_END])
    ] : [
      PerfMonitor.Race([QUICK_CHART_REQUEST_START, CREATIVE_LIST_REQUEST_START, AD_INFO_REQUEST_START]),
      PerfMonitor.All([QUICK_CHART_REQUEST_END, CREATIVE_LIST_REQUEST_END, AD_INFO_REQUEST_END])
    ]
  }
}

export const KEYWORD_TTI_METRIC_SETTING = {
  // html: html_connect -> html_ttfb -> html_download
  html: ['nav.fetchStart', 'nav.responseEnd'],
  html_connect: ['nav.fetchStart', 'nav.requestStart'],
  html_ttfb: ['nav.requestStart', 'nav.responseStart'],
  html_download: ['nav.responseStart', 'nav.responseEnd'],

  // init: init_script_eval -> init_mount -> init_other
  init: ['nav.responseEnd', REQUEST_START],
  init_script_eval: ['nav.responseEnd', BEFORE_ENTRY_RUN],
  init_mount: [BEFORE_ENTRY_RUN, APP_MOUNTED],
  init_other: [APP_MOUNTED, REQUEST_START],

  // list request
  request: [REQUEST_START, REQUEST_END],

  // table render
  render: [REQUEST_END, RENDER_END],

  // FMP: html -> init(promotionMounted)
  fmp: ['nav.fetchStart', APP_MOUNTED],
  // TTI: html -> init -> request -> render
  total: ['nav.fetchStart', RENDER_END]
}

// keyword list perf
export const keywordListMonitor = new PerfMonitor(
  KEYWORD_LIST_PERF,
  REQUEST_RENDER_METRIC_SETTING,
  { enable: false }
)

// keyword tti perf
export const keywordTTIMonitor = new PerfMonitor(
  KEYWORD_TTI_PERF,
  KEYWORD_TTI_METRIC_SETTING,
  {
    once: true,
    enable: false,
    onReported: () => {
      setTimeout(() => { keywordListMonitor.config({ enable: true }) })
    }
  }
)

// ad tti perf
export const adTTIMonitor = new PerfMonitor(
  AD_TTI_PERF,
  GENERAL_TTI_METRIC_SETTING,
  { once: true }
)

export const getAdTags = () => ({
  ...COMMON_STATIC_TAGS,
  campaign_type: String(GLOBAL_VAR?.campaignType) || '',
  landing_type: String(window.AD_ENVS?.landingType) || '',
  marketing_scene: String(GLOBAL_VAR?.marketingScene) || '',
  delivery_related_num: String(GLOBAL_VAR?.deliveryRelatedNum) || '',
  delivery_mode: String(GLOBAL_VAR?.deliveryMode) || '',
  batch_copy_count: String(window.AD_ENVS?.copyAdCount) || '0',
  page_operate_type: window.AD_ENVS?.isBatchCopy
    ? 'batch_copy'
    : window.AD_ENVS?.isCopy
      ? 'copy'
      : window.AD_ENVS?.isEdit
        ? 'edit'
        : 'new'
})

// creative tti perf
export const creativeTTIMonitor = new PerfMonitor(
  CREATIVE_TTI_PERF,
  GENERAL_TTI_METRIC_SETTING,
  { once: true }
)

export const getCreativeTags = () => ({
  ...COMMON_STATIC_TAGS,
  campaign_type: String(GLOBAL_VAR.campaignType) || '',
  landing_type: String(GLOBAL_VAR.landingType) || '',
  marketing_scene: String(GLOBAL_VAR.marketingScene) || '',
  delivery_related_num: String(GLOBAL_VAR.deliveryRelatedNum) || '',
  delivery_mode: String(GLOBAL_VAR.deliveryMode) || '',
  page_operate_type:
    window.AD_ENVS?.copyNumber > 1
      ? 'batch_copy'
      : window.AD_ENVS?.isCopy
        ? 'copy'
        : window.AD_ENVS?.isUpdate
          ? 'edit'
          : 'new'
})
